import { LitElement, html } from 'lit';
import typographyStyles from '@cof/fs-typography-core/lib/typography.lit';
import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
import { fs24Tag } from '@cof/fs-icons';
import '@cof/fs-container-wc/fs-container.js';
import { TagKey } from '@cof/tag-catalog-refi-user-entry-mfe';
import { DebugService } from '../../services/debug/debug.service.js';
import { style } from './ease-offer.component.style.js';
import '@cof/fs-modal-dialog-wc/fs-modal-dialog.js';
import { banner } from './assets/ease-offer.js';
import SessionService from '../../services/session/session.service.js';
import { CustomerOffersService } from '../../services/customer-offers/customer-offers.service.js';
import { MatchingState } from '../../models/search-customer-offers.model.js';
import { stratum } from '../../constants/stratum.js';
import { REFI_WEB_UI_CONST } from '../../constants/constants.js';
import { StorageService } from '../../services/storage/storage.service.js';
import { HostAppEventsService } from '../../utils/host-app-events-service.js';
import { redirectToLanding } from '../../utils/redirect-to-landing.js';

class RefiEaseOffer extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiEaseOfferComponent');
    this.hasApps = false;
    this.loadingPage = true;
    this.loadingAfterContinueClicked = false;
    this.promotionalCode = '';
    this.helpNumber = '1.888.263.4582';

    FsIcon.add(fs24Tag);
  }

  static styles = [style, typographyStyles];

  static properties = {
    hasApps: { type: Boolean, attribute: 'has-apps', reflect: true },
    loadingPage: { type: Boolean, state: true },
    loadingAfterContinueClicked: { type: Boolean },
  };

  async connectedCallback() {
    super.connectedCallback();
    document.dispatchEvent(new CustomEvent('show-progress-bar', { detail: { displayBar: false } }));
    // Parse the external_id to get the APR, promotional code, and expiration date
    this.parseExternalId().then(() => {
      // Hide the loading spinner and show the appropriate page (splash or error) when complete
      this.loadingPage = false;
    });
  }

  async parseExternalId() {
    // Get external_id from the SessionStorage and break it into parts (last 3 should be apr, promo code, and expiration date)
    const externalId = SessionService.getExternalId();
    const parts = externalId ? externalId.split('_') : null;

    // If external_id is valid, get the promotional code and check if it matches the promotional code in the customer offers
    if (parts && parts.length >= 3) {
      // Promo code in the external_id
      this.promotionalCode = parts[parts.length - 2];

      // Get the customer offers with the call to the refi-host-app-bff
      const offerDetails = await CustomerOffersService.getCustomerOffers({}, MatchingState.primarySso);

      // Check if we got a valid response from the API call
      if (offerDetails !== null) {
        // Check if the promotional code in the customer offers matches the one in the SessionStorage
        if (offerDetails.promotionalCode === this.promotionalCode) {
          // All the criteria for GREASE splash page are met, so the page can be shown
          this.showErrorPage = false;
          this.noExternalId = false;

          // Set the APR that the page will display
          this.apr = this.getAprFromParts(parts);

          // Set the expiration date that the page will display
          this.expirationDate = this.generateExpiryDate(parts[parts.length - 1]);

          // Save the offer details to the session storage so that it's available for the application
          SessionService.setCachedOffer(offerDetails);
          StorageService.setShowOfferBannerInSession(false);
          stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_EASE_OFFER);
        } else {
          // Promotional code is expired or does not match, so take them to the landing page instead
          this.redirecting = true;
          stratum.publishTag(TagKey.GREASE_ROUTING_CODB_MATCH_FAIL_REDIRECT_TO_GR_CONTROL_LANDING_PAGE_FLOW);
          redirectToLanding(REFI_WEB_UI_CONST.EASE_GR_LANDING_PAGE, 'external_id', externalId);
        }
      } else {
        // No proper response from the CO API (technical difficulty)
        this.showErrorPage = true;
        this.noExternalId = false;
        stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_EASE_OFFER_2);
      }
    } else {
      // No external_id in SessionStorage
      this.showErrorPage = true;
      this.noExternalId = true;
      stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_EASE_OFFER_2);
      stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_EASE_OFFER_3);
    }
  }

  getAprFromParts(parts) {
    const apr = parts[parts.length - 3];
    return Number(apr && apr.indexOf('d') > -1 ? apr.replace('d', '.') : apr);
  }

  // Method to convert a date in YYYYMMDD format to a human-readable date (Month DD, YYYY)
  generateExpiryDate(expirationDate) {
    const year = expirationDate.slice(0, 4);
    const month = expirationDate.slice(4, 6);
    const day = expirationDate.slice(6, 8);
    const parsedDate = new Date(year, parseInt(month, 10) - 1, day);
    return parsedDate?.toLocaleDateString('en-us', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  continueButtonClicked() {
    // Publish the tag for the continue button click
    stratum.publishTag(TagKey.EASE_OFFER_PAGE_CONTINUE_CLICK);

    // Make the continue button show the loading spinner on it
    this.loadingAfterContinueClicked = true;
    document.dispatchEvent(new CustomEvent('sign-out-toggle', { detail: { showSignOut: true } }));
    // If the user has apps, go to the pick-an-app page, otherwise go to the new application page
    if (this.hasApps) {
      const params = new URLSearchParams(window.location.search);
      this.debug.log('dispatching next-step-event');
      HostAppEventsService.sendNextStepEvent({ step: 'pick-an-app', data: params.toString() ? params.toString() : '' });
    } else {
      HostAppEventsService.sendStartNewAppEvent();
    }
  }

  tryAgainLaterLinkClicked() {
    // Publish the tag for the try again link click
    stratum.publishTag(TagKey.EASE_OFFER_PAGE_ERROR_PAGE_ACCESS_CODE_LINK_CLICK);
  }

  render() {
    return html` ${this.redirecting
      ? html``
      : html` ${this.loadingPage
          ? html` <refi-loading-spinner class="_gb-show"></refi-loading-spinner> `
          : html` ${this.showErrorPage
              ? html` ${this.noExternalId
                  ? html`
                      <div class="error-container">
                        <fs-heading class="fs-text-size--medium-3 error-title" level="1" size="medium" variant="primary">We’ve hit a snag</fs-heading>

                        <fs-body-text class="error-body" size="normal"
                          >We’re having technical difficulties, but your offer is still valid. Please give us a call at
                          <a class="phone-number-link" href="tel:${this.helpNumber.replace(/\./g, '-')}">${this.helpNumber}</a>
                          (Monday–Friday 9a.m.–9p.m. ET) to get your offer details.</fs-body-text
                        >
                      </div>
                    `
                  : html`
                      <div class="error-container">
                        <fs-heading class="fs-text-size--medium-3 error-title" level="1" size="medium" variant="primary">We’ve hit a snag</fs-heading>

                        <fs-body-text class="error-body" size="normal">We’re having technical difficulties, but your offer is still valid. Please save your unique access code for your records.</fs-body-text>

                        <fs-container border="" elevation="0" radius="large">
                          <div class="wrapper">
                            <div class="fs-icon-flex">
                              <fs-icon icon="fs-24-tag" variant="primary"></fs-icon>
                            </div>

                            <div class="promo-header-column">
                              <fs-heading class="error-access-code" size="normal" level="2" variant="primary">Access code</fs-heading>
                              <div>
                                <fs-body-text size="normal" class="prefilled-access-code">${this.promotionalCode}</fs-body-text>
                              </div>
                            </div>
                          </div>
                        </fs-container>

                        <fs-body-text class="error-try-again-later" size="normal"
                          >You can try again later by visiting <a class="fs-link fs-link--primary" href="https://autorefi.capitalone.com/offer" @click="${this.tryAgainLaterLinkClicked}">https://autorefi.capitalone.com/offer</a></fs-body-text
                        >
                      </div>
                    `}`
              : html`
                  <div class="banner">${banner}</div>

                  <div class="full-page">
                    <div class="header">
                      <fs-heading class="title-text" level="1" size="large">Your rate is</fs-heading>
                      <fs-heading class="title-text" level="1" size="large">ready to go!</fs-heading>
                    </div>

                    <div class="body">
                      <fs-body-text class="body-text" size="normal">Next, we’ll check your eligibility for auto refinancing so you can see personalized savings options with your real rate.</fs-body-text>
                    </div>

                    <div class="ease-offer-card">
                      <div class="rate-container">
                        <div>
                          <div class="fs-overline-text fs-overline-text--normal">YOUR RATE:</div>
                        </div>

                        <div>
                          <div class="apr-container">
                            <span class="apr-number">${this.apr}</span>
                            <div class="apr-stack"><span class="apr-sign">%</span><span class="apr-text">APR</span></div>
                          </div>
                        </div>

                        <div class="expiration-body">
                          <fs-body-text class="expiration-text" size="small">Expires ${this.expirationDate}</fs-body-text>
                        </div>

                        <fs-button variant="primary" type="button" class="continue-button" ?loading="${this.loadingAfterContinueClicked}" @click="${this.continueButtonClicked}">Continue</fs-button>
                      </div>
                    </div>
                  </div>
                `}`}`}`;
  }
}

customElements.define('refi-ease-offer', RefiEaseOffer);
